import React from 'react';
import type {CourseAssignmentSimpleResponse} from 'src/api/ProactApiClient';
import {CourseAssignmentOverviewContent} from 'src/app/training/CourseAssignmentOverviewContent';
import {CourseAssignmentRowPills} from 'src/app/training/courseAssignmentPillConfig';
import {Text} from 'src/design/atoms/Text';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {SideDrawer} from 'src/design/organisms/SideDrawer';
import {useGetCourseAssignmentActiveSessionQuery} from 'src/redux/queries/courseAssignment';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface CoursePreviewDrawerProps {
  item: CourseAssignmentSimpleResponse | undefined;
  opened: boolean;
  onClose: () => void;
}

interface CoursePreviewProps {
  item: CourseAssignmentSimpleResponse;
}

const CoursePreview: React.FC<CoursePreviewProps> = ({item}) => {
  const {
    data: tracker,
    isLoading,
    error,
  } = useGetCourseAssignmentActiveSessionQuery(item.id!, {
    refetchOnMountOrArgChange: true,
  });

  return (
    <>
      <SideDrawer.Header
        title={item.courseDefinition?.name ?? 'Unknown'}
        badges={CourseAssignmentRowPills(item)}
        fullPageHref={PathUtility.TrainingCourseOverview(String(item.id))}
      />
      <SideDrawer.Body loading={isLoading}>
        {error ? (
          <ErrorAlert mt={0} message={parseErrorMessage(error)} />
        ) : tracker ? (
          <CourseAssignmentOverviewContent tracker={tracker} />
        ) : (
          !isLoading && (
            <Text c="dimmed" size="sm">
              Failed to load course session. Please try again later.
            </Text>
          )
        )}
      </SideDrawer.Body>
    </>
  );
};

/** Dashboard course preview: the course overview body, with the start/continue link to the session. */
export const CoursePreviewDrawer: React.FC<CoursePreviewDrawerProps> = ({
  item,
  opened,
  onClose,
}) => {
  if (!item) {
    return null;
  }

  return (
    <SideDrawer
      data-testid="course-preview-drawer"
      opened={opened}
      onClose={onClose}>
      <CoursePreview key={item.id} item={item} />
    </SideDrawer>
  );
};
